import type { Role } from '../useAuthSession'
import { API_BASE } from '../api'
import { supabase } from '../supabaseClient'
import { higherRole } from '../useAuthSession'

// Shapes returned by /api/v1/admin/*. Distinct from the public read-shape in
// `availability.ts` because the admin path includes `id` (so the editor knows
// which row to PUT) and uses the un-flattened junction-row form straight from
// the supabase nested-select.

export interface AdminSport {
  id: number
  name: string
  slug: string | null
  category: 'Motor Sports' | 'Combat Sports' | 'Action Sports' | 'Alternative Sports'
  logo_url: string | null
  brand_color: string | null
  secondary_color: string | null
  tertiary_color: string | null
  /** 4th slot — drives the availability map's prediction layer. */
  alternate_color: string | null
  sport_states: { state_code: string }[]
  sport_international: { region: string }[]
}

export interface AdminSportsbook {
  id: number
  name: string
  status: string | null
  brand_color: string | null
  logo_url: string | null
  logo_fill: boolean
  // Book-level full regulatory statement (migration 0047) — offer cards fall
  // back to this when the offer has no override. '' = not set.
  regulatory_full: string
  // Book-level general affiliate link (migration 0058) — the GENERAL
  // distribution grid + general availability map tiles open this URL.
  // '' = not set; tiles render unlinked.
  general_url: string
  sportsbook_states: { state_code: string }[]
  sportsbook_international: { region: string, kind: 'allowed' | 'blocked' }[]
}

// Sub-affiliate (migration 0059) — internal-only record. `slug` is the
// embed flag widgets pass as ?aff= / subAffiliate; links are per book.
export interface AdminSubAffiliate {
  id: number
  name: string
  slug: string
  created_at: string
  updated_at: string
  sub_affiliate_sports: { sport_id: number }[]
  sub_affiliate_links: { sportsbook_id: number, url: string }[]
}

export interface UpdateSubAffiliatePayload {
  name: string
  slug: string
  sport_ids: number[]
  links: { sportsbook_id: number, url: string }[]
}

export interface AuditRow {
  id: number
  ts: string
  actor: string | null
  action: string
  table_name: string
  before: unknown
  after: unknown
}

export interface UpdateSportPayload {
  name: string
  slug: string | null
  approved: string[]
  international: string[]
}

export interface UpdateSportsbookPayload {
  name: string
  status: string | null
  active_states: string[]
  international: string[]
  international_blocked?: string[]
}

export interface SportDivision {
  id: number
  sport_id: number
  name: string
}

// Who trades a carried (sport, sportsbook[, division]) pair.
// 'us' = the AltSports trading team supplies the odds (the default),
// 'book' = the sportsbook trades it in-house. Not carried = no row.
export type TradedBy = 'us' | 'book'

export interface SportCarrier {
  sportsbook_id: number
  sportsbook_name: string | null
  division_id: number | null
  division_name: string | null
  traded_by: TradedBy
}

export interface SportsbookCarried {
  sport_id: number
  sport_name: string | null
  division_id: number | null
  division_name: string | null
  traded_by: TradedBy
}

export interface CarrierSlot {
  sportsbook_id: number
  division_id?: number | null
  traded_by?: TradedBy
}

export interface CarriedSlot {
  sport_id: number
  division_id?: number | null
  traded_by?: TradedBy
}

export interface SportAffiliateLink {
  sportsbook_id: number
  url: string
}

export interface SportBrandPayload {
  logo_url: string | null
  brand_color: string | null
  secondary_color: string | null
  tertiary_color: string | null
  alternate_color: string | null
}

// Offers — /affiliate sport-page editor (persisted since migration 0044).
export interface OfferRegion {
  code: string
  name: string
}

export interface AdminOffer {
  id: number
  sportsbook_id: number | null
  company_label: string
  headline: string
  terms: string
  // Per-offer regulatory override; '' = inherit the book's statement.
  regulatory_full: string
  cta_text: string
  cta_url: string
  logo_url: string
  brand_hex: string | null
  status: string
  sort_order: number
  expires_at: string | null
  offer_regions: OfferRegion[]
}

export interface AdminOfferContainer {
  id: number
  name: string
  sport_id: number | null
  offers: AdminOffer[]
}

// Write shape for the full-replace save: id > 0 keeps the row, null inserts.
export interface OfferWriteEntry {
  id: number | null
  sportsbook_id: number | null
  company_label: string
  headline: string
  terms: string
  regulatory_full: string
  cta_text: string
  cta_url: string
  logo_url: string
  brand_hex: string | null
  status: string
  expires_at: string | null
  regions: OfferRegion[]
}

export interface SportsbookOffer {
  id: number
  headline: string
  status: string
  cta_url: string
  expires_at: string | null
  offer_containers: {
    id: number
    name: string
    sports: { name: string, slug: string | null } | null
  } | null
}

class AdminAuthError extends Error {
  constructor() {
    super('admin auth required')
    this.name = 'AdminAuthError'
  }
}

async function adminFetch(path: string, init?: RequestInit): Promise<Response> {
  // The bearer token is now the Supabase JWT — sourced from the live session
  // rather than localStorage. supabase-js auto-refreshes, so this always
  // returns a non-expired token (or null if signed out).
  const { data } = await supabase.auth.getSession()
  const token = data.session?.access_token
  if (!token) throw new AdminAuthError()

  // FormData bodies (logo uploads) must NOT get a Content-Type — the browser
  // sets multipart/form-data with the boundary itself.
  const isFormData = typeof FormData !== 'undefined' && init?.body instanceof FormData
  const headers = {
    Authorization: `Bearer ${token}`,
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    ...((init?.headers as Record<string, string> | undefined) ?? {}),
  }

  const res = await fetch(`${API_BASE}${path}`, { ...init, headers })
  if (res.status === 401) throw new AdminAuthError()
  return res
}

export async function fetchAdminSports(): Promise<AdminSport[]> {
  const res = await adminFetch('/api/v1/admin/sports')
  if (!res.ok) throw new Error(`fetch sports failed: ${res.status}`)
  return res.json()
}

export async function fetchAdminSportsbooks(): Promise<AdminSportsbook[]> {
  const res = await adminFetch('/api/v1/admin/sportsbooks')
  if (!res.ok) throw new Error(`fetch sportsbooks failed: ${res.status}`)
  return res.json()
}

export interface FetchAuditOptions {
  limit?: number
  beforeId?: number | null
  actor?: string
  tableName?: string
}

export async function fetchAdminAudit(opts: FetchAuditOptions = {}): Promise<AuditRow[]> {
  const params = new URLSearchParams()
  params.set('limit', String(opts.limit ?? 50))
  if (opts.beforeId != null && opts.beforeId > 0) {
    params.set('before_id', String(opts.beforeId))
  }
  if (opts.actor && opts.actor.trim()) {
    params.set('actor', opts.actor.trim())
  }
  if (opts.tableName) {
    params.set('table', opts.tableName)
  }
  const res = await adminFetch(`/api/v1/admin/audit?${params.toString()}`)
  if (!res.ok) throw new Error(`fetch audit failed: ${res.status}`)
  return res.json()
}

export interface CreateSportPayload {
  name: string
  slug: string | null
  category: AdminSport['category']
}

export interface CreatedSport {
  id: number
  name: string
  slug: string | null
  category: AdminSport['category']
}

export async function createAdminSport(payload: CreateSportPayload): Promise<CreatedSport> {
  const res = await adminFetch('/api/v1/admin/sports', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
  if (!res.ok) {
    const body = await res.text()
    // 409 = duplicate name/slug; bubble the server message so the dialog can
    // tell the user exactly what collided.
    throw new Error(res.status === 409 ? `That name or slug already exists.` : `create sport failed: ${res.status} ${body}`)
  }
  return res.json()
}

export async function updateAdminSport(id: number, payload: UpdateSportPayload): Promise<unknown> {
  const res = await adminFetch(`/api/v1/admin/sports/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  })
  if (!res.ok) {
    const body = await res.text()
    throw new Error(`update sport ${id} failed: ${res.status} ${body}`)
  }
  return res.json()
}

export async function updateAdminSportsbook(id: number, payload: UpdateSportsbookPayload): Promise<unknown> {
  const res = await adminFetch(`/api/v1/admin/sportsbooks/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  })
  if (!res.ok) {
    const body = await res.text()
    throw new Error(`update sportsbook ${id} failed: ${res.status} ${body}`)
  }
  return res.json()
}

export async function fetchAdminSportDivisions(): Promise<SportDivision[]> {
  const res = await adminFetch('/api/v1/admin/sport-divisions')
  if (!res.ok) throw new Error(`fetch sport-divisions failed: ${res.status}`)
  return res.json()
}

export async function fetchSportCarriers(sportId: number): Promise<SportCarrier[]> {
  const res = await adminFetch(`/api/v1/admin/sports/${sportId}/carriers`)
  if (!res.ok) throw new Error(`fetch sport carriers failed: ${res.status}`)
  return res.json()
}

export async function updateSportCarriers(sportId: number, carriers: CarrierSlot[]): Promise<unknown> {
  const res = await adminFetch(`/api/v1/admin/sports/${sportId}/carriers`, {
    method: 'PUT',
    body: JSON.stringify({ carriers }),
  })
  if (!res.ok) {
    const body = await res.text()
    throw new Error(`update sport carriers failed: ${res.status} ${body}`)
  }
  return res.json()
}

export async function fetchSportsbookCarried(sportsbookId: number): Promise<SportsbookCarried[]> {
  const res = await adminFetch(`/api/v1/admin/sportsbooks/${sportsbookId}/carried`)
  if (!res.ok) throw new Error(`fetch sportsbook carried failed: ${res.status}`)
  return res.json()
}

export async function updateSportsbookCarried(sportsbookId: number, carried: CarriedSlot[]): Promise<unknown> {
  const res = await adminFetch(`/api/v1/admin/sportsbooks/${sportsbookId}/carried`, {
    method: 'PUT',
    body: JSON.stringify({ carried }),
  })
  if (!res.ok) {
    const body = await res.text()
    throw new Error(`update sportsbook carried failed: ${res.status} ${body}`)
  }
  return res.json()
}

export async function fetchSportAffiliateLinks(sportId: number): Promise<SportAffiliateLink[]> {
  const res = await adminFetch(`/api/v1/admin/sports/${sportId}/affiliate-links`)
  if (!res.ok) throw new Error(`fetch sport affiliate-links failed: ${res.status}`)
  return res.json()
}

export async function updateSportBrand(sportId: number, payload: SportBrandPayload): Promise<unknown> {
  const res = await adminFetch(`/api/v1/admin/sports/${sportId}/brand`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  })
  if (!res.ok) {
    const body = await res.text()
    throw new Error(`update sport brand failed: ${res.status} ${body}`)
  }
  return res.json()
}

export async function updateSportsbookRegulatory(sportsbookId: number, regulatoryFull: string): Promise<unknown> {
  const res = await adminFetch(`/api/v1/admin/sportsbooks/${sportsbookId}/regulatory`, {
    method: 'PUT',
    body: JSON.stringify({ regulatory_full: regulatoryFull }),
  })
  if (!res.ok) {
    const body = await res.text()
    throw new Error(`update regulatory failed: ${res.status} ${body}`)
  }
  return res.json()
}

export async function updateSportsbookGeneralUrl(sportsbookId: number, generalUrl: string): Promise<unknown> {
  const res = await adminFetch(`/api/v1/admin/sportsbooks/${sportsbookId}/general-link`, {
    method: 'PUT',
    body: JSON.stringify({ general_url: generalUrl }),
  })
  if (!res.ok) {
    const body = await res.text()
    throw new Error(`update general link failed: ${res.status} ${body}`)
  }
  return res.json()
}

// ── Operator onboarding (migration 0066) ──────────────────────────────────

export interface CreateSportsbookPayload {
  name: string
  brand_color: string | null
  logo_fill: boolean
}

export interface CreatedSportsbook {
  id: number
  name: string
  status: string | null
  brand_color: string | null
  logo_url: string | null
  logo_fill: boolean
}

export async function createAdminSportsbook(payload: CreateSportsbookPayload): Promise<CreatedSportsbook> {
  const res = await adminFetch('/api/v1/admin/sportsbooks', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
  if (!res.ok) {
    const body = await res.text()
    throw new Error(res.status === 409 ? 'An operator with that name already exists.' : `create operator failed: ${res.status} ${body}`)
  }
  return res.json()
}

export interface SportsbookBrandPayload {
  brand_color: string | null
  // Repo-static path (/sportsbooks/<file>) or an https URL (Storage upload).
  logo_url: string | null
  logo_fill: boolean
}

export async function updateSportsbookBrand(sportsbookId: number, payload: SportsbookBrandPayload): Promise<unknown> {
  const res = await adminFetch(`/api/v1/admin/sportsbooks/${sportsbookId}/brand`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  })
  if (!res.ok) {
    const body = await res.text()
    throw new Error(`update brand for sportsbook ${sportsbookId} failed: ${res.status} ${body}`)
  }
  return res.json()
}

export interface UploadedLogo {
  sportsbook_id: number
  brand_color: string | null
  logo_url: string
  logo_fill: boolean
  storage_path: string
}

export const LOGO_ACCEPT = 'image/png,image/jpeg,image/webp,image/svg+xml,image/gif'
export const LOGO_MAX_BYTES = 2 * 1024 * 1024

/**
 * Upload a logo tile for a sportsbook. The API validates type + signature,
 * stores it in the public `brand-logos` bucket and writes the CDN URL to
 * logo_url. `logoFill` (optional) is persisted in the same write.
 */
export async function uploadSportsbookLogo(sportsbookId: number, file: File, logoFill?: boolean): Promise<UploadedLogo> {
  const form = new FormData()
  form.append('file', file, file.name)
  if (logoFill !== undefined)
    form.append('logo_fill', logoFill ? 'true' : 'false')
  const res = await adminFetch(`/api/v1/admin/sportsbooks/${sportsbookId}/logo`, {
    method: 'POST',
    body: form,
  })
  if (!res.ok) {
    let detail = ''
    try {
      detail = ((await res.json()) as { error?: string }).error ?? ''
    }
    catch { /* non-JSON body */ }
    throw new Error(detail ? `logo upload failed: ${detail}` : `logo upload failed: ${res.status}`)
  }
  return res.json()
}

export interface UploadedSportLogo {
  sport_id: number
  logo_url: string
  brand_color: string | null
  secondary_color: string | null
  tertiary_color: string | null
  alternate_color: string | null
  storage_path: string
}

/**
 * Upload a league logo for a sport — the sport twin of uploadSportsbookLogo.
 * The API validates type + signature, stores the file in the public
 * `brand-logos` bucket (sports/<id>-<epoch>.<ext>) and writes the CDN URL to
 * sports.logo_url through the same brand RPC the LOGO text field uses, so the
 * audit log shows the old → new URL. Colors are left untouched.
 */
export async function uploadSportLogo(sportId: number, file: File): Promise<UploadedSportLogo> {
  const form = new FormData()
  form.append('file', file, file.name)
  const res = await adminFetch(`/api/v1/admin/sports/${sportId}/logo`, {
    method: 'POST',
    body: form,
  })
  if (!res.ok) {
    let detail = ''
    try {
      detail = ((await res.json()) as { error?: string }).error ?? ''
    }
    catch { /* non-JSON body */ }
    throw new Error(detail ? `logo upload failed: ${detail}` : `logo upload failed: ${res.status}`)
  }
  return res.json()
}

export async function fetchSubAffiliates(): Promise<AdminSubAffiliate[]> {
  const res = await adminFetch('/api/v1/admin/sub-affiliates')
  if (!res.ok) throw new Error(`fetch sub-affiliates failed: ${res.status}`)
  return res.json()
}

export async function createSubAffiliate(name: string, slug: string): Promise<unknown> {
  const res = await adminFetch('/api/v1/admin/sub-affiliates', {
    method: 'POST',
    body: JSON.stringify({ name, slug }),
  })
  if (!res.ok) {
    const body = await res.text()
    throw new Error(res.status === 409 ? `flag "${slug}" is already taken` : `create sub-affiliate failed: ${res.status} ${body}`)
  }
  return res.json()
}

export async function updateSubAffiliate(id: number, payload: UpdateSubAffiliatePayload): Promise<unknown> {
  const res = await adminFetch(`/api/v1/admin/sub-affiliates/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  })
  if (!res.ok) {
    const body = await res.text()
    throw new Error(res.status === 409 ? `flag "${payload.slug}" is already taken` : `update sub-affiliate failed: ${res.status} ${body}`)
  }
  return res.json()
}

export async function deleteSubAffiliate(id: number): Promise<unknown> {
  const res = await adminFetch(`/api/v1/admin/sub-affiliates/${id}`, {
    method: 'DELETE',
  })
  if (!res.ok) {
    const body = await res.text()
    throw new Error(`delete sub-affiliate failed: ${res.status} ${body}`)
  }
  return res.json()
}

export async function fetchSportOfferContainers(sportId: number): Promise<AdminOfferContainer[]> {
  const res = await adminFetch(`/api/v1/admin/sports/${sportId}/offer-containers`)
  if (!res.ok) throw new Error(`fetch offer containers failed: ${res.status}`)
  return res.json()
}

export async function createOfferContainer(sportId: number, name: string): Promise<{ id: number, name: string }> {
  const res = await adminFetch('/api/v1/admin/offer-containers', {
    method: 'POST',
    body: JSON.stringify({ sport_id: sportId, name }),
  })
  if (!res.ok) {
    const body = await res.text()
    throw new Error(`create container failed: ${res.status} ${body}`)
  }
  return res.json()
}

export async function renameOfferContainer(containerId: number, name: string): Promise<unknown> {
  const res = await adminFetch(`/api/v1/admin/offer-containers/${containerId}`, {
    method: 'PUT',
    body: JSON.stringify({ name }),
  })
  if (!res.ok) {
    const body = await res.text()
    throw new Error(`rename container failed: ${res.status} ${body}`)
  }
  return res.json()
}

export async function deleteOfferContainer(containerId: number): Promise<unknown> {
  const res = await adminFetch(`/api/v1/admin/offer-containers/${containerId}`, {
    method: 'DELETE',
  })
  if (!res.ok) {
    const body = await res.text()
    throw new Error(`delete container failed: ${res.status} ${body}`)
  }
  return res.json()
}

export async function updateContainerOffers(containerId: number, offers: OfferWriteEntry[]): Promise<unknown> {
  const res = await adminFetch(`/api/v1/admin/offer-containers/${containerId}/offers`, {
    method: 'PUT',
    body: JSON.stringify({ offers }),
  })
  if (!res.ok) {
    const body = await res.text()
    throw new Error(`save offers failed: ${res.status} ${body}`)
  }
  return res.json()
}

export async function fetchSportsbookOffers(sportsbookId: number): Promise<SportsbookOffer[]> {
  const res = await adminFetch(`/api/v1/admin/sportsbooks/${sportsbookId}/offers`)
  if (!res.ok) throw new Error(`fetch sportsbook offers failed: ${res.status}`)
  return res.json()
}

export async function updateSportAffiliateLinks(sportId: number, links: SportAffiliateLink[]): Promise<unknown> {
  const res = await adminFetch(`/api/v1/admin/sports/${sportId}/affiliate-links`, {
    method: 'PUT',
    body: JSON.stringify({ links }),
  })
  if (!res.ok) {
    const body = await res.text()
    throw new Error(`update sport affiliate-links failed: ${res.status} ${body}`)
  }
  return res.json()
}

export interface AdminConfigListItem {
  slug: string
  displayName: string
  category: string
  updated_at: string
  updated_by: string | null
}

export interface AdminConfigDetail {
  slug: string
  config: Record<string, unknown>
  updated_at: string
  updated_by: string | null
}

export async function fetchConfigList(): Promise<{ configs: AdminConfigListItem[] }> {
  const res = await adminFetch('/api/v1/admin/configs')
  if (!res.ok) throw new Error(`fetch configs failed: ${res.status}`)
  return res.json()
}

export async function fetchConfig(slug: string): Promise<AdminConfigDetail> {
  const res = await adminFetch(`/api/v1/admin/configs/${slug}`)
  if (!res.ok) throw new Error(`fetch config ${slug} failed: ${res.status}`)
  return res.json()
}

// Markets Traded — trader-curated list of market names for one sport. Read is
// just a string[]; the write sends the full replacement list (trim/dedupe is
// handled server-side by the RPC).
export async function fetchSportMarketsTraded(sportId: number): Promise<string[]> {
  const res = await adminFetch(`/api/v1/admin/sports/${sportId}/markets-traded`)
  if (!res.ok) throw new Error(`fetch markets-traded failed: ${res.status}`)
  return res.json()
}

export async function updateSportMarketsTraded(sportId: number, markets: string[]): Promise<unknown> {
  const res = await adminFetch(`/api/v1/admin/sports/${sportId}/markets-traded`, {
    method: 'PUT',
    body: JSON.stringify({ markets }),
  })
  if (!res.ok) {
    const body = await res.text()
    throw new Error(`update markets-traded failed: ${res.status} ${body}`)
  }
  return res.json()
}

// Trading notes — the per-sport TRADING tab sections. GET always returns all
// seven sections (empty ones have content '' and null timestamps); PUT writes
// one section and returns its saved shape. Trading+ only, both directions.
export const TRADING_NOTE_SECTIONS = [
  { key: 'about', label: 'ABOUT' },
  { key: 'markets_vig', label: 'MARKETS & VIG' },
  { key: 'schedule', label: 'ODDS SCHEDULE' },
  { key: 'client_delivery', label: 'CLIENT DELIVERY' },
  { key: 'grading', label: 'GRADING & RESULTS' },
  { key: 'sources', label: 'SOURCES & LINKS' },
  { key: 'notes', label: 'NOTES' },
] as const

export type TradingNoteSection = (typeof TRADING_NOTE_SECTIONS)[number]['key']

export interface TradingNote {
  section: TradingNoteSection
  content: string
  updated_at: string | null
  updated_by: string | null
}

export async function fetchSportTradingNotes(sportId: number): Promise<TradingNote[]> {
  const res = await adminFetch(`/api/v1/admin/sports/${sportId}/trading-notes`)
  if (!res.ok) throw new Error(`fetch trading-notes failed: ${res.status}`)
  const body = await res.json() as { sections: TradingNote[] }
  return body.sections
}

export async function updateSportTradingNote(
  sportId: number,
  section: TradingNoteSection,
  content: string,
): Promise<TradingNote> {
  const res = await adminFetch(`/api/v1/admin/sports/${sportId}/trading-notes/${section}`, {
    method: 'PUT',
    body: JSON.stringify({ content }),
  })
  if (!res.ok) {
    const body = await res.text()
    throw new Error(`update trading-notes failed: ${res.status} ${body}`)
  }
  return res.json()
}

// Trading emails — per-sport TO / CC / BCC recipient lists backing the
// "COMPOSE EMAIL →" shortcut on the /trading page. Trading+ both directions.
export interface TradingEmails {
  to_emails: string[]
  cc_emails: string[]
  bcc_emails: string[]
  updated_at: string | null
  updated_by: string | null
}

// Trading links — per-sport quick-reference buttons on the /trading page
// (LINKS section). group_name '' = ungrouped; row order is display order.
export interface TradingLink {
  group_name: string
  label: string
  url: string
}

export interface TradingLinks {
  links: TradingLink[]
  updated_at: string | null
  updated_by: string | null
}

export async function fetchSportTradingLinks(sportId: number): Promise<TradingLinks> {
  const res = await adminFetch(`/api/v1/admin/sports/${sportId}/trading-links`)
  if (!res.ok) throw new Error(`fetch trading-links failed: ${res.status}`)
  return res.json()
}

export async function updateSportTradingLinks(
  sportId: number,
  links: TradingLink[],
): Promise<unknown> {
  const res = await adminFetch(`/api/v1/admin/sports/${sportId}/trading-links`, {
    method: 'PUT',
    body: JSON.stringify({ links }),
  })
  if (!res.ok) {
    const body = await res.text()
    throw new Error(`update trading-links failed: ${res.status} ${body}`)
  }
  return res.json()
}

export async function fetchSportTradingEmails(sportId: number): Promise<TradingEmails> {
  const res = await adminFetch(`/api/v1/admin/sports/${sportId}/trading-emails`)
  if (!res.ok) throw new Error(`fetch trading-emails failed: ${res.status}`)
  return res.json()
}

export async function updateSportTradingEmails(
  sportId: number,
  payload: { to_emails: string[], cc_emails: string[], bcc_emails: string[] },
): Promise<unknown> {
  const res = await adminFetch(`/api/v1/admin/sports/${sportId}/trading-emails`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  })
  if (!res.ok) {
    const body = await res.text()
    throw new Error(`update trading-emails failed: ${res.status} ${body}`)
  }
  return res.json()
}

// Trading pages — global (non-sport) plain-text pages on the /trading
// route. Only slug 'sparket' exists today.
export interface TradingPage {
  slug: string
  content: string
  updated_at: string | null
  updated_by: string | null
}

export async function fetchTradingPage(slug: string): Promise<TradingPage> {
  const res = await adminFetch(`/api/v1/admin/trading-pages/${slug}`)
  if (!res.ok) throw new Error(`fetch trading page ${slug} failed: ${res.status}`)
  return res.json()
}

export async function updateTradingPage(slug: string, content: string): Promise<unknown> {
  const res = await adminFetch(`/api/v1/admin/trading-pages/${slug}`, {
    method: 'PUT',
    body: JSON.stringify({ content }),
  })
  if (!res.ok) {
    const body = await res.text()
    throw new Error(`update trading page ${slug} failed: ${res.status} ${body}`)
  }
  return res.json()
}

// Athletes — per-sport roster with birthdates. The trading process checks
// ages before offering athlete props, so the UI groups by age bracket.
export interface SportAthlete {
  id: number
  name: string
  // YYYY-MM-DD, or null when the birthdate is unknown.
  birthdate: string | null
  // On the sport's current active roster (trader-maintained).
  is_active: boolean
}

export interface AthleteWritePayload {
  name: string
  birthdate?: string | null
  // Absent reads as false server-side — writers editing name/birthdate
  // must echo the current flag or they'll clear it.
  is_active?: boolean
}

export async function fetchSportAthletes(sportId: number): Promise<SportAthlete[]> {
  const res = await adminFetch(`/api/v1/admin/sports/${sportId}/athletes`)
  if (!res.ok) throw new Error(`fetch athletes failed: ${res.status}`)
  const body = await res.json() as { athletes: SportAthlete[] }
  return body.athletes
}

export async function createSportAthlete(sportId: number, payload: AthleteWritePayload): Promise<{ id: number }> {
  const res = await adminFetch(`/api/v1/admin/sports/${sportId}/athletes`, {
    method: 'POST',
    body: JSON.stringify(payload),
  })
  if (!res.ok) {
    const body = await res.text()
    // 409 = duplicate athlete name within this sport.
    throw new Error(res.status === 409
      ? `An athlete named "${payload.name}" already exists for this sport.`
      : `create athlete failed: ${res.status} ${body}`)
  }
  return res.json()
}

export async function updateSportAthlete(sportId: number, athleteId: number, payload: AthleteWritePayload): Promise<unknown> {
  const res = await adminFetch(`/api/v1/admin/sports/${sportId}/athletes/${athleteId}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  })
  if (!res.ok) {
    const body = await res.text()
    throw new Error(res.status === 409
      ? `An athlete named "${payload.name}" already exists for this sport.`
      : `update athlete failed: ${res.status} ${body}`)
  }
  return res.json()
}

export async function deleteSportAthlete(sportId: number, athleteId: number): Promise<unknown> {
  const res = await adminFetch(`/api/v1/admin/sports/${sportId}/athletes/${athleteId}`, {
    method: 'DELETE',
  })
  if (!res.ok) {
    const body = await res.text()
    throw new Error(`delete athlete failed: ${res.status} ${body}`)
  }
  return res.json()
}

export { AdminAuthError }

// ── Integrations (migration 0069) ─────────────────────────────────────
// Feed-integration market matrix from the trading sheet. Read-only in the
// portal: the sheet is edited outside and re-seeded.
//
// Who sees the INTEGRATIONS tab and the per-sport INTEGRATIONS panel. Owner-
// only until trading signs off; flip to 'user' here AND in api/integrations.ts
// (this constant only hides UI — the API enforces the same minimum).
export const INTEGRATIONS_MIN_ROLE: Role = 'owner'

// Who can edit (trading owns this knowledge, like Markets Traded). Effective
// write gate is the higher of view + edit so nobody can edit what they can't
// see — i.e. owner-only while the preview lasts. Twin of api/integrations.ts.
export const INTEGRATIONS_EDIT_ROLE: Role = 'trading'
export const INTEGRATIONS_WRITE_ROLE: Role = higherRole(INTEGRATIONS_MIN_ROLE, INTEGRATIONS_EDIT_ROLE)

export type IntegrationStatus = 'live' | 'qa' | 'test' | 'not_offered' | 'unknown'

export interface IntegrationMarket {
  market_key: string
  market_label: string
  status: IntegrationStatus
  needs_review: boolean
  review_note: string | null
}

export interface IntegrationLeague {
  league_key: string
  league_name: string
  // Catalog sport the seed mapped this league onto; null when the sheet
  // lists a league the catalog does not have (BYB, Nitrocross, …).
  sport_id: number | null
  sport_slug: string | null
  sport_name: string | null
  sport_category: string | null
  markets: IntegrationMarket[]
}

export interface Integration {
  id: number
  key: string
  name: string
  sort_order: number
  leagues: IntegrationLeague[]
}

export interface SportIntegration {
  integration_id: number
  integration_key: string
  integration_name: string
  sort_order: number
  league_key: string
  league_name: string
  markets: IntegrationMarket[]
}

export async function fetchIntegrations(): Promise<Integration[]> {
  const res = await adminFetch('/api/v1/admin/integrations')
  if (!res.ok) throw new Error(`fetch integrations failed: ${res.status}`)
  return res.json()
}

export async function fetchSportIntegrations(sportId: number): Promise<SportIntegration[]> {
  const res = await adminFetch(`/api/v1/admin/sports/${sportId}/integrations`)
  if (!res.ok) throw new Error(`fetch sport integrations failed: ${res.status}`)
  return res.json()
}

// ── Integrations writes (migration 0070) ─────────────────────────────

export interface IntegrationMarketInput {
  market_key: string
  market_label: string
  status: IntegrationStatus
  needs_review: boolean
  review_note: string | null
}

export interface IntegrationLeagueInput {
  league_name: string
  sport_id: number | null
  markets: IntegrationMarketInput[]
}

async function readError(res: Response, fallback: string): Promise<string> {
  try {
    const body = await res.json() as { error?: string }
    return body.error ? `${fallback}: ${body.error}` : `${fallback} (${res.status})`
  }
  catch {
    return `${fallback} (${res.status})`
  }
}

export async function createIntegration(key: string, name: string): Promise<{ id: number, key: string, name: string }> {
  const res = await adminFetch('/api/v1/admin/integrations', {
    method: 'POST',
    body: JSON.stringify({ key, name }),
  })
  if (!res.ok) throw new Error(await readError(res, 'create integration failed'))
  return res.json()
}

export async function renameIntegration(id: number, name: string): Promise<unknown> {
  const res = await adminFetch(`/api/v1/admin/integrations/${id}`, {
    method: 'PUT',
    body: JSON.stringify({ name }),
  })
  if (!res.ok) throw new Error(await readError(res, 'rename integration failed'))
  return res.json()
}

export async function deleteIntegration(id: number): Promise<unknown> {
  const res = await adminFetch(`/api/v1/admin/integrations/${id}`, { method: 'DELETE' })
  if (!res.ok) throw new Error(await readError(res, 'delete integration failed'))
  return res.json()
}

/** Full replace of one league under one integration. Empty markets removes the league. */
export async function updateIntegrationLeague(
  integrationId: number,
  leagueKey: string,
  payload: IntegrationLeagueInput,
): Promise<unknown> {
  const res = await adminFetch(`/api/v1/admin/integrations/${integrationId}/leagues/${encodeURIComponent(leagueKey)}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  })
  if (!res.ok) throw new Error(await readError(res, 'save league failed'))
  return res.json()
}

/** Live state as CSV (same columns as integration_markets.csv). Caller triggers the download. */
export async function fetchIntegrationsCsv(): Promise<Blob> {
  const res = await adminFetch('/api/v1/admin/integrations?format=csv')
  if (!res.ok) throw new Error(`export failed: ${res.status}`)
  return res.blob()
}

// Sheet-style key from a label: "Head-to-Head" → head_to_head,
// "Podium/Top3" → podium_top3, "Match Set Handicap (2-way)" → match_set_handicap_2_way.
export function integrationKeyFromLabel(label: string): string {
  return label
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
}

// ── Event calendar (embed Overview column) ───────────────────────────
// Read-only proxy of the AltSportsData public API — GET
// /api/v1/admin/event-calendar (user+). Shape mirrors api/eventCalendar.ts.

export interface CalendarEvent {
  id: string
  /** AltSportsData competitionKey, e.g. `gsoc`. */
  key: string
  competition: string
  sport: string
  /** Our catalog slug, or null when the league has no catalog row. */
  slug: string | null
  name: string
  tour: string | null
  startDate: string
  endDate: string | null
  status: string | null
  location: string | null
  locationGroup: string | null
}

export interface CalendarLeague {
  key: string
  name: string
  sport: string
  slug: string | null
  events: number
}

export interface EventCalendar {
  source: 'altsportsdata'
  fetchedAt: string
  windowDays: number
  stale: boolean
  leagues: CalendarLeague[]
  failed: string[]
  events: CalendarEvent[]
}

export async function fetchEventCalendar(days = 30): Promise<EventCalendar> {
  const res = await adminFetch(`/api/v1/admin/event-calendar?days=${encodeURIComponent(String(days))}`)
  if (!res.ok) {
    const body = await res.json().catch(() => null) as { error?: string } | null
    throw new Error(body?.error ?? `event calendar failed: ${res.status}`)
  }
  return res.json()
}
